import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Edit,
  Cancel,
  Save,
  VerifiedUser,
  LocalHospital,
  BadgeOutlined,
  PhoneOutlined,
  EmailOutlined,
  HomeOutlined,
  ReceiptLong,
  CalendarToday,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../lib/axios';

// ─── Schema ───────────────────────────────────────────────────────────────────

const updateProfileSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  phoneNumber: z.string().min(10, 'Please enter a valid phone number'),
  email: z.string().email('Please enter a valid email').optional().or(z.literal('')),
  clinicName: z.string().optional().or(z.literal('')),
  rncTaxId: z.string().optional().or(z.literal('')),
  clinicAddress: z.string().optional().or(z.literal('')),
});

type UpdateProfileFormData = z.infer<typeof updateProfileSchema>;

interface DoctorProfile {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  licenseNumber: string;
  phoneNumber: string;
  email?: string;
  specialty?: { id: string; name: string };
  avatarUrl?: string;
  clinicName?: string;
  rncTaxId?: string;
  clinicAddress?: string;
  cmdNumber?: string;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const NAVY = '#0D1B35';
const BLUE = '#0066CC';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontSize: 14,
    bgcolor: '#FAFBFD',
    '& fieldset': { borderColor: '#E1E8ED', borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: '#C1C9D2' },
    '&.Mui-focused fieldset': { borderColor: BLUE, borderWidth: '2px' },
    '&.Mui-focused': { bgcolor: '#fff' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: BLUE },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const InfoRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: string;
  fallback?: string;
}> = ({ icon, label, value, fallback = '—' }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.05)', '&:last-child': { borderBottom: 'none', pb: 0 } }}>
    <Box sx={{ color: 'rgba(0,0,0,0.3)', mt: '2px', flexShrink: 0 }}>{icon}</Box>
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.6px', mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, color: value ? 'text.primary' : 'text.disabled', fontWeight: value ? 500 : 400 }}>
        {value || fallback}
      </Typography>
    </Box>
  </Box>
);

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box
    sx={{
      bgcolor: '#fff',
      borderRadius: 3,
      border: '1px solid rgba(0,0,0,0.06)',
      overflow: 'hidden',
    }}
  >
    <Box sx={{ px: 3, py: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.8px' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ px: 3, py: 2 }}>
      {children}
    </Box>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────

export const DoctorProfile: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const { data: profile, isLoading } = useQuery<DoctorProfile>({
    queryKey: ['doctorProfile'],
    queryFn: async () => {
      const { data } = await apiClient.get<DoctorProfile>('/doctors/me');
      return data;
    },
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<UpdateProfileFormData>({
    resolver: zodResolver(updateProfileSchema),
    values: profile
      ? {
          firstName: profile.firstName,
          lastName: profile.lastName,
          phoneNumber: profile.phoneNumber,
          email: profile.email || '',
          clinicName: profile.clinicName || '',
          rncTaxId: profile.rncTaxId || '',
          clinicAddress: profile.clinicAddress || '',
        }
      : undefined,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: UpdateProfileFormData) => {
      const { data: updated } = await apiClient.put<DoctorProfile>(`/doctors/${profile?.id}`, data);
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctorProfile'] });
      setIsEditing(false);
      setSuccess('Perfil actualizado correctamente.');
      setError(null);
      setTimeout(() => setSuccess(null), 3000);
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Error al actualizar el perfil.');
      setSuccess(null);
    },
  });

  const handleCancel = () => { reset(); setIsEditing(false); setError(null); };

  // ── Loading ──
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress sx={{ color: BLUE }} />
      </Box>
    );
  }

  if (!profile) {
    return <Alert severity="error">Doctor profile not found</Alert>;
  }

  const initials = `${profile.firstName?.[0] ?? ''}${profile.lastName?.[0] ?? ''}`.toUpperCase();

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>

      {/* ── Hero Banner ─────────────────────────────────────────────── */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          pt: { xs: 4, sm: 5 },
          px: { xs: 3, sm: 4 },
          pb: 0,
          position: 'relative',
          mb: '-44px',
        }}
      >
        {/* Decorative circles */}
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.06)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: 20, right: 80, width: 100, height: 100, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

        {/* Edit button top-right */}
        {!isEditing && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Edit sx={{ fontSize: 14 }} />}
            onClick={() => setIsEditing(true)}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              borderColor: 'rgba(255,255,255,0.25)',
              color: 'rgba(255,255,255,0.75)',
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px',
              py: 0.5,
              '&:hover': { borderColor: '#fff', color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
            }}
          >
            Editar
          </Button>
        )}

        {/* Accent rule */}
        <Box sx={{ width: 32, height: 3, borderRadius: 2, background: `linear-gradient(90deg, ${BLUE} 0%, #00A86B 100%)`, mb: 2 }} />

        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', mb: 0.5 }}>
          {profile.specialty?.name ?? 'Médico'}
        </Typography>
        <Typography variant="h2" sx={{ color: '#fff', mb: 3 }}>
          Dr. {profile.firstName} {profile.lastName}
        </Typography>

        {/* Avatar anchored to bottom */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
          <Avatar
            src={profile.avatarUrl}
            sx={{
              width: 88,
              height: 88,
              bgcolor: BLUE,
              fontSize: '1.6rem',
              fontWeight: 800,
              border: '3px solid rgba(255,255,255,0.15)',
              mb: '-44px',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {initials}
          </Avatar>

          <Box sx={{ pb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {profile.isVerified && (
              <Chip
                icon={<VerifiedUser sx={{ fontSize: '13px !important', color: '#00A86B !important' }} />}
                label="Verificado"
                size="small"
                sx={{ bgcolor: 'rgba(0,168,107,0.15)', color: '#00A86B', fontSize: 11, fontWeight: 600, border: '1px solid rgba(0,168,107,0.25)' }}
              />
            )}
            <Chip
              label={`Lic. ${profile.licenseNumber}`}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600 }}
            />
            {profile.cmdNumber && (
              <Chip
                label={`CMD ${profile.cmdNumber}`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 600 }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Content ─────────────────────────────────────────────────── */}
      <Box sx={{ pt: '96px', display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* Alerts */}
        {success && <Alert severity="success" sx={{ borderRadius: 2, fontSize: 13 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ borderRadius: 2, fontSize: 13 }}>{error}</Alert>}

        <Box
          component={isEditing ? 'form' : 'div'}
          onSubmit={isEditing ? handleSubmit((d) => updateMutation.mutate(d)) : undefined}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {/* ── Personal Information ────────────────────────────────── */}
          <SectionCard title="Información Personal">
            {isEditing ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField fullWidth label="Nombre" error={!!errors.firstName} helperText={errors.firstName?.message} {...register('firstName')} sx={fieldSx} />
                <TextField fullWidth label="Apellido" error={!!errors.lastName} helperText={errors.lastName?.message} {...register('lastName')} sx={fieldSx} />
                <TextField fullWidth label="Teléfono" error={!!errors.phoneNumber} helperText={errors.phoneNumber?.message} {...register('phoneNumber')} sx={fieldSx} />
                <TextField fullWidth label="Correo (opcional)" error={!!errors.email} helperText={errors.email?.message} {...register('email')} sx={fieldSx} />
              </Box>
            ) : (
              <>
                <InfoRow icon={<BadgeOutlined sx={{ fontSize: 18 }} />} label="Nombre" value={`${profile.firstName} ${profile.lastName}`} />
                <InfoRow icon={<PhoneOutlined sx={{ fontSize: 18 }} />} label="Teléfono" value={profile.phoneNumber} />
                <InfoRow icon={<EmailOutlined sx={{ fontSize: 18 }} />} label="Correo" value={user?.email} />
              </>
            )}
          </SectionCard>

          {/* ── Clinic Information ──────────────────────────────────── */}
          <SectionCard title={t('doctor.profile.clinicInfo')}>
            {isEditing ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField fullWidth label={t('doctor.profile.clinicName')} error={!!errors.clinicName} helperText={errors.clinicName?.message} {...register('clinicName')} sx={fieldSx} />
                <TextField fullWidth label={t('doctor.profile.rncTaxId')} error={!!errors.rncTaxId} helperText={errors.rncTaxId?.message} {...register('rncTaxId')} sx={fieldSx} />
                <TextField fullWidth label={t('doctor.profile.clinicAddress')} error={!!errors.clinicAddress} helperText={errors.clinicAddress?.message} {...register('clinicAddress')} sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }} />
              </Box>
            ) : (
              <>
                <InfoRow icon={<LocalHospital sx={{ fontSize: 18 }} />} label={t('doctor.profile.clinicName')} value={profile.clinicName} />
                <InfoRow icon={<ReceiptLong sx={{ fontSize: 18 }} />} label={t('doctor.profile.rncTaxId')} value={profile.rncTaxId} />
                <InfoRow icon={<HomeOutlined sx={{ fontSize: 18 }} />} label={t('doctor.profile.clinicAddress')} value={profile.clinicAddress} />
              </>
            )}
          </SectionCard>

          {/* ── Account Info ────────────────────────────────────────── */}
          <SectionCard title="Cuenta">
            <InfoRow icon={<CalendarToday sx={{ fontSize: 18 }} />} label="Miembro desde" value={new Date(profile.createdAt).toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' })} />
          </SectionCard>

          {/* ── Action Buttons ──────────────────────────────────────── */}
          {isEditing && (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={updateMutation.isPending ? <CircularProgress size={14} color="inherit" /> : <Save sx={{ fontSize: 16 }} />}
                disabled={updateMutation.isPending}
                sx={{
                  bgcolor: NAVY,
                  borderRadius: '10px',
                  fontWeight: 700,
                  fontSize: 13,
                  py: 1.25,
                  px: 2.5,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#1a2d4a' },
                  '&.Mui-disabled': { bgcolor: 'rgba(0,0,0,0.08)' },
                }}
              >
                {updateMutation.isPending ? 'Guardando...' : 'Guardar cambios'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Cancel sx={{ fontSize: 16 }} />}
                onClick={handleCancel}
                disabled={updateMutation.isPending}
                sx={{
                  borderColor: 'rgba(0,0,0,0.15)',
                  color: 'text.secondary',
                  borderRadius: '10px',
                  fontWeight: 600,
                  fontSize: 13,
                  py: 1.25,
                  px: 2.5,
                  textTransform: 'none',
                  '&:hover': { borderColor: 'rgba(0,0,0,0.3)', bgcolor: 'transparent' },
                }}
              >
                Cancelar
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
